import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./createPassword.css";
import { IoMdCheckmarkCircle } from "react-icons/io";
import { Eye, EyeOff, Lock } from "lucide-react";
import { MdCancel } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import password_mark from "../../assets/password_mark.png";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

 

const clearOnboardingState = () => {
  localStorage.removeItem("onboarding_code");
  localStorage.removeItem("onboarding_completed");
  localStorage.removeItem("booking_settings");
  localStorage.removeItem("selectedNiche");
  localStorage.removeItem("selectedNicheId");
  localStorage.removeItem("selectedNicheName");
  localStorage.removeItem("selectedSubNiches");
  localStorage.removeItem("selectedServicesPayload");
  localStorage.removeItem("salonData");
  localStorage.removeItem("real_estate_mode");
  localStorage.removeItem("working_days_closed");
  localStorage.removeItem("booking_settings_closed");
  localStorage.removeItem("my_services_closed");
  localStorage.removeItem("suggested_services_closed");
  localStorage.removeItem("worker_biography_saved");
  localStorage.removeItem("worker_name");
  localStorage.removeItem("worker_phone");
  localStorage.removeItem("worker_country");

  sessionStorage.removeItem("onboarding_force_route");
  sessionStorage.removeItem("onboarding_force_status");
};

export default function CreatePassword() {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showRepeat, setShowRepeat] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const email = localStorage.getItem("pendingEmail");

  const registrationStartedRef = useRef(false);

 

  const gtagSafe = (event, params) => {
    if (window.gtag) window.gtag("event", event, params);
  };

  const trackStartRegistration = () => {
    if (!registrationStartedRef.current) {
      gtagSafe("start_form_registration");
      registrationStartedRef.current = true;
    }
  };

  const trackRegistrationError = () => gtagSafe("registration_form_error");
  const trackSignInError = () => gtagSafe("sign_in_form_error");
  const trackSignUp = () => gtagSafe("sign_up", { method: "Email" });
  const trackLogin = () => gtagSafe("login", { method: "Email" });

  const handlePasswordChange = (e) => {
    const value = e.target.value;

    if (/\s/.test(value)) {
      const passwordWithoutSpaces = value.replace(/\s/g, "");
      setPassword(passwordWithoutSpaces);

      if (value.trim() !== value) toast.error(t("auth.errorSpacesNotAllowed"));
      return;
    }

    setPassword(value);
  };

  const handlePasswordKeyDown = (e) => {
    if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      toast.error(t("auth.errorSpacesNotAllowed"));
    }
  };

  const handleRepeatPasswordChange = (e) => {
    const value = e.target.value;

    if (/\s/.test(value)) {
      const passwordWithoutSpaces = value.replace(/\s/g, "");
      setRepeatPassword(passwordWithoutSpaces);

      if (value.trim() !== value) toast.error(t("auth.errorSpacesNotAllowed"));
      return;
    }

    setRepeatPassword(value);
  };

  const conditions = [
    { label: t("auth.conditionLength"), valid: password.length >= 8 && password.length <= 20 },
    { label: t("auth.conditionNumber"), valid: /\d/.test(password) && !/\s/.test(password) },
    { label: t("auth.conditionUppercase"), valid: /[A-Z]/.test(password) && !/\s/.test(password) },
    { label: t("auth.conditionNoSpaces"), valid: !/\s/.test(password) },
  ];

  const isFormValid = conditions.every((c) => c.valid) && password === repeatPassword && !/\s/.test(password);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid || !email) {
      trackRegistrationError();
      toast.error(t("auth.errorFillFields"));
      return;
    }

    setLoading(true);

    try {
      const setPasswordUrl = `${BASE_URL}/auth/public-set-password/`;
      const res = await fetch(setPasswordUrl, {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify({
          email,
          password,
          repeat_password: repeatPassword,
          timeZone: getUserTimeZone(),
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        trackRegistrationError();
        toast.error(data?.detail || t("auth.errorPasswordSetupFailed"));
        return;
      }

      trackSignUp();
      toast.success(t("auth.successPasswordCreated"));
      localStorage.setItem("user_email", email);

      const tokenObtainUrl = `${BASE_URL}/auth/token/obtain/`;
      const loginRes = await fetch(tokenObtainUrl, {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify({
          email,
          password,
          timeZone: getUserTimeZone(),
        }),
      });

      const loginData = await loginRes.json().catch(() => ({}));

      if (!loginRes.ok || !loginData?.access) {
        trackSignInError();
        toast.error(t("auth.errorLoginFailed"));
        return;
      }

      clearOnboardingState();
      trackLogin();

      localStorage.setItem("accessToken", loginData.access);
      localStorage.setItem("refreshToken", loginData.refresh);
      localStorage.setItem("user_status", loginData.user_status);

      if (loginData.user_status === "worker") {
        localStorage.setItem("user_status", "worker");
        localStorage.setItem("onboarding_code", "234");
        sessionStorage.setItem("onboarding_force_route", "/worker-biography");
        sessionStorage.setItem("onboarding_force_status", "worker");

        navigate("/worker-biography", { replace: true });
        return;
      }

      navigate("/account-type-select", { replace: true });
    } catch (err) {
      trackRegistrationError();
      console.error("❌ CREATE PASSWORD ERROR:", err);
      toast.error(t("auth.errorNetwork"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleEnterKey = (e) => {
      if (e.key === "Enter" && isFormValid && !loading) {
        e.preventDefault();
        handleSubmit(e);
      }
    };
    window.addEventListener("keydown", handleEnterKey);
    return () => window.removeEventListener("keydown", handleEnterKey);
  }, [isFormValid, loading]);

  return (
    <div className="password">
      <main className="password-main">
        <div className="password-card">
          <img src={password_mark} alt="password" className="password-avatar" />
          <h2 className="password-title">{t("auth.createPasswordTitle")}</h2>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <label className="password-label">{t("auth.passwordLabel")}</label>
            <div className="input-wrapper">
              <Lock className="icon" />
              <input
                type={showPass ? "text" : "password"}
                value={password}
                placeholder={t("auth.enterPassword")}
                onChange={(e) => {
                  trackStartRegistration();
                  handlePasswordChange(e);
                }}
                required
                autoFocus
              />
              <button type="button" className="eye-btn" onClick={() => setShowPass(!showPass)}>
                {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {password && (
              <ul className="password-conditions">
                {conditions.map((c, i) => (
                  <li key={i} className={c.valid ? "valid" : "invalid"}>
                    {c.valid ? <IoMdCheckmarkCircle /> : <MdCancel />} {c.label}
                  </li>
                ))}
              </ul>
            )}
            <label className="password-label">{t("auth.repeatPasswordLabel")}</label>
            <div className="input-wrapper">
              <Lock className="icon" />
              <input
                type={showRepeat ? "text" : "password"}
                value={repeatPassword}
                placeholder={t("auth.repeatPassword")}
                onChange={handleRepeatPasswordChange}
                required
              />
              <button type="button" className="eye-btn" onClick={() => setShowRepeat(!showRepeat)}>
                {showRepeat ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <button type="submit" className="password-btn" disabled={!isFormValid || loading}>
              {loading ? t("auth.processing") : t("auth.next")}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}